import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from '@/components/ui/button';
import { NAV_LINKS, BUSINESS_NAME, COMPANY_DETAILS } from '@/src/constants';
import { cn } from '@/lib/utils';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 flex flex-col">
      {/* Top Utility Bar */}
      <div className={cn(
        "w-full bg-[#050505] border-b border-white/[0.05] transition-all duration-500 ease-in-out overflow-hidden",
        scrolled ? "h-0 opacity-0" : "h-10 opacity-100"
      )}>
        <div className="container mx-auto px-6 lg:px-12 h-full flex items-center justify-between">
          <div className="flex items-center gap-4 text-muted-foreground/60">
            <div className="flex items-center gap-2">
              <MapPin className="w-3 h-3 text-accent/50" />
              <span className="text-[10px] font-medium uppercase tracking-wider">
                DMV Area: DC · VA · MD
              </span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a 
              href={`tel:${COMPANY_DETAILS.phone.replace(/\s+/g, '')}`} 
              className="flex items-center gap-2 text-muted-foreground/60 hover:text-foreground transition-colors group"
            >
              <Phone className="w-3 h-3 text-accent/40 group-hover:text-accent transition-colors" />
              <span className="text-[10px] font-medium tracking-wide whitespace-nowrap">
                {COMPANY_DETAILS.phone}
              </span>
            </a>
            <a 
              href={`mailto:${COMPANY_DETAILS.email}`} 
              className="flex items-center gap-2 text-muted-foreground/60 hover:text-foreground transition-colors group"
            >
              <Mail className="w-3 h-3 text-accent/40 group-hover:text-accent transition-colors" />
              <span className="text-[10px] font-medium tracking-wide">
                {COMPANY_DETAILS.email}
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className={cn(
          "w-full transition-all duration-500 ease-in-out",
          scrolled 
            ? "py-4 bg-background/95 backdrop-blur-md border-b border-white/[0.08] shadow-lg" 
            : "py-6 bg-transparent"
        )}
      >
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between">
            {/* Brand Identity */}
            <Link to="/" className="flex items-center gap-3 group shrink-0">
              <div className="relative w-10 h-10 flex items-center justify-center bg-accent/5 border border-accent/10 rounded-lg transition-colors group-hover:border-accent/30">
                <Shield className="w-5 h-5 text-accent" />
              </div>
              <span className="font-display font-bold text-xl md:text-2xl tracking-tight text-foreground">
                {BUSINESS_NAME}
              </span>
            </Link>

            {/* Navigation Links */}
            <div className="hidden lg:flex items-center gap-10">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={cn(
                    "text-[11px] font-bold uppercase tracking-[0.2em] transition-colors hover:text-accent relative py-1",
                    location.pathname === link.href ? "text-accent" : "text-muted-foreground/80"
                  )}
                >
                  {link.name}
                  {location.pathname === link.href && (
                    <motion.span 
                      layoutId="nav-underline"
                      className="absolute -bottom-1 left-0 w-full h-[1.5px] bg-accent rounded-full" 
                    />
                  )}
                </Link>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-6">
              <div className={cn(
                "hidden xl:flex flex-col items-end transition-all duration-500",
                scrolled ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1 pointer-events-none"
              )}>
                <span className="text-[10px] font-bold text-foreground tracking-tight">{COMPANY_DETAILS.phone}</span>
                <span className="text-[8px] font-bold text-accent uppercase tracking-widest">Consulting Line</span>
              </div>
              
              <Button asChild variant="accent" size="sm" className="hidden md:flex font-bold uppercase tracking-widest text-[10px] px-8 h-11 rounded-lg transition-all hover:shadow-md active:scale-[0.98]">
                <Link to="/contact">Request Security Review</Link>
              </Button>

              {/* Mobile Menu Toggle */}
              <button
                className="lg:hidden text-foreground/80 p-2 hover:text-accent transition-colors"
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Toggle menu"
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Overlay */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="lg:hidden absolute top-full left-0 right-0 bg-background border-b border-white/[0.08] shadow-2xl overflow-hidden"
            >
              <div className="container mx-auto px-6 py-10 flex flex-col gap-8">
                <div className="flex flex-col gap-4">
                  {NAV_LINKS.map((link) => (
                    <Link
                      key={link.name}
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      className={cn(
                        "text-lg font-display font-bold uppercase tracking-widest py-2 transition-colors",
                        location.pathname === link.href ? "text-accent" : "text-muted-foreground/60"
                      )}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
                
                <div className="pt-8 border-t border-white/[0.05] space-y-6">
                  <div className="grid grid-cols-1 gap-4">
                    <a href={`tel:${COMPANY_DETAILS.phone}`} className="flex items-center gap-3 text-muted-foreground">
                      <Phone className="w-4 h-4 text-accent/60" />
                      <span className="text-sm font-bold tracking-widest">{COMPANY_DETAILS.phone}</span>
                    </a>
                    <a href={`mailto:${COMPANY_DETAILS.email}`} className="flex items-center gap-3 text-muted-foreground">
                      <Mail className="w-4 h-4 text-accent/60" />
                      <span className="text-sm font-bold tracking-widest lowercase">{COMPANY_DETAILS.email}</span>
                    </a>
                  </div>
                  <Button asChild variant="accent" className="w-full h-14 rounded-xl font-bold uppercase tracking-widest text-xs">
                    <Link to="/contact" onClick={() => setIsOpen(false)}>Request Security Review</Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}
